/* ============================================
   CHAPTER CONTENT COMPONENT
   The Creative Orchestra - GEMINI Framework
   Renders markdown chapter content with styling
   ============================================ */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { getChapterById } from '@data';

/**
 * Chapter Content Component
 * Renders markdown content with custom styling
 * 
 * @param {Object} props
 * @param {string} props.content - Markdown content string
 * @param {Object} props.chapter - Chapter metadata
 * @param {string} props.className - Additional CSS classes
 */
const ChapterContent = ({ 
  content, 
  chapter,
  className = '',
}) => {
  // Custom markdown components
  const markdownComponents = useMemo(() => ({
    // Headings
    h1: ({ children }) => (
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold 
        text-white mt-12 mb-6 leading-tight">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl md:text-3xl font-display font-semibold 
        text-white mt-10 mb-4 leading-tight">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl md:text-2xl font-display font-medium 
        text-white mt-8 mb-3">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-lg md:text-xl font-semibold text-white mt-6 mb-2">
        {children}
      </h4>
    ),

    // Paragraphs
    p: ({ children }) => (
      <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-6 
        font-serif">
        {children}
      </p>
    ),

    // Emphasis
    em: ({ children }) => (
      <em className="text-gray-200 italic">{children}</em>
    ),
    strong: ({ children }) => (
      <strong className="text-white font-semibold">{children}</strong>
    ),

    // Blockquotes
    blockquote: ({ children }) => (
      <blockquote className="relative my-8 pl-6 py-4 
        border-l-4 border-indigo-500/50
        bg-white/5 rounded-r-lg">
        <div className="text-lg md:text-xl text-gray-300 italic font-serif">
          {children}
        </div>
      </blockquote>
    ),

    // Lists
    ul: ({ children }) => (
      <ul className="list-disc list-outside ml-6 mb-6 space-y-2 text-gray-300">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-outside ml-6 mb-6 space-y-2 text-gray-300">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-base md:text-lg leading-relaxed pl-2">
        {children}
      </li>
    ),

    // Code
    code: ({ inline, children }) => {
      if (inline) {
        return (
          <code className="px-1.5 py-0.5 rounded bg-white/10 
            text-indigo-300 font-mono text-sm">
            {children}
          </code>
        );
      }
      return (
        <code className="block p-4 rounded-lg bg-black/40 
          text-gray-300 font-mono text-sm overflow-x-auto">
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className="my-6 rounded-lg overflow-hidden">
        {children}
      </pre>
    ),

    // Links
    a: ({ href, children }) => (
      <a 
        href={href}
        className="text-indigo-400 hover:text-indigo-300 
          underline underline-offset-2 transition-colors"
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    ),

    // Horizontal rule
    hr: () => (
      <hr className="my-12 border-0 h-px bg-gradient-to-r 
        from-transparent via-white/20 to-transparent" />
    ),

    // Small text (for disclaimers, fine print)
    small: ({ children }) => (
      <small className="block text-xs text-gray-500 italic leading-relaxed 
        my-6 opacity-75">
        {children}
      </small>
    ),

    // Images (if any in markdown)
    img: ({ src, alt }) => (
      <figure className="my-8">
        <img 
          src={src} 
          alt={alt}
          className="w-full rounded-lg border border-white/10"
          loading="lazy"
        />
        {alt && (
          <figcaption className="mt-2 text-center text-sm text-gray-500 italic">
            {alt}
          </figcaption>
        )}
      </figure>
    ),

    // Tables
    table: ({ children }) => (
      <div className="my-8 overflow-x-auto">
        <table className="w-full border-collapse">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="border-b border-white/20">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody className="divide-y divide-white/10">
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr className="hover:bg-white/5 transition-colors">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="px-4 py-3 text-left text-sm font-semibold text-white">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-3 text-sm text-gray-300">
        {children}
      </td>
    ),
  }), []);

  return (
    <motion.article
      className={`prose-novel ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Chapter header */}
      {chapter && (
        <ChapterHeader chapter={chapter} />
      )}

      {/* Markdown content */}
      <div className="chapter-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={markdownComponents}
        >
          {content}
        </ReactMarkdown>
      </div>
    </motion.article>
  );
};

/**
 * Chapter Header Component
 * Displays chapter number, title, and metadata
 */
const ChapterHeader = ({ chapter }) => {
  const isPartStart = chapter.type === 'prologue' || 
    (chapter.number === parseInt(chapter.partTitle?.match(/\d+/)?.[0] || 0));

  return (
    <header className="text-center mb-12 pb-8 border-b border-white/10">
      {/* Part header (if first chapter of part) */}
      {chapter.partTitle && (
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <span className="text-sm text-indigo-400 tracking-wider uppercase">
            {chapter.partTitle}
          </span>
          {chapter.partSubtitle && (
            <p className="text-xs text-gray-500 mt-1">
              {chapter.partSubtitle}
            </p>
          )}
        </motion.div>
      )}

      {/* Chapter number */}
      <motion.div
        className="mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        {chapter.type === 'prologue' ? (
          <span className="text-sm text-gray-500 tracking-widest uppercase">
            Prologue
          </span>
        ) : chapter.type === 'epilogue' ? (
          <span className="text-sm text-gray-500 tracking-widest uppercase">
            Epilogue
          </span>
        ) : (
          <span className="text-sm text-gray-500 tracking-widest">
            Chapter {chapter.number}
          </span>
        )}
      </motion.div>

      {/* Chapter title */}
      <motion.h1
        className="text-3xl md:text-4xl lg:text-5xl font-display font-bold 
          text-white leading-tight"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        {chapter.title}
      </motion.h1>

      {/* Subtitle */}
      {chapter.subtitle && (
        <motion.p
          className="text-xl text-gray-400 mt-3 italic"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {chapter.subtitle}
        </motion.p>
      )}

      {/* Reading time */}
      {chapter.readingTime && (
        <motion.p
          className="text-sm text-gray-600 mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          {chapter.readingTime} read
        </motion.p>
      )}
    </header>
  );
};

/**
 * Chapter Summary Card
 * Displays chapter summary in a card format
 */
export const ChapterSummary = ({ chapter, className = '' }) => {
  if (!chapter?.summary) return null;

  return (
    <div className={`p-6 rounded-xl bg-white/5 border border-white/10 ${className}`}>
      <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
        Summary
      </h4>
      <p className="text-gray-300 leading-relaxed">
        {chapter.summary}
      </p>
    </div>
  );
};

/**
 * Chapter Loading Skeleton
 */
export const ChapterSkeleton = () => {
  return (
    <div className="animate-pulse">
      {/* Header skeleton */}
      <div className="text-center mb-12 pb-8 border-b border-white/10">
        <div className="h-4 w-24 bg-white/10 rounded mx-auto mb-4" />
        <div className="h-10 w-3/4 bg-white/10 rounded mx-auto mb-3" />
        <div className="h-6 w-1/2 bg-white/10 rounded mx-auto" />
      </div>

      {/* Content skeleton */}
      <div className="space-y-4">
        {[...Array(8)].map((_, i) => (
          <div 
            key={i} 
            className="h-4 bg-white/10 rounded"
            style={{ width: `${70 + Math.random() * 30}%` }}
          />
        ))}
        <div className="h-4 bg-white/10 rounded w-3/4" />
        <div className="h-20 my-8" /> {/* Spacer */}
        {[...Array(6)].map((_, i) => (
          <div 
            key={i + 10} 
            className="h-4 bg-white/10 rounded"
            style={{ width: `${70 + Math.random() * 30}%` }}
          />
        ))}
      </div>
    </div>
  );
};

export default ChapterContent;
